import moment, { Moment } from 'moment-timezone';
import { DepositBankAccount } from './deposit-bank-account';
import { LegFiJwtService } from '../../services/auth/legfi-jwt.service';
import { FixedAsset } from './accounting/fixed-asset';
import { PlaidToken } from './plaid-token';
import { Organization } from './organization';
import { AllianceCredential } from './alliance/alliance-credential';
import { BankReconciliation } from './bank-reconciliation';
import { LobBankAccount } from './lob-bank-account';

export interface HistoricalBalance
{
    createdAt: string;
    balance: number;
}

export class UnifiedBankAccount
{
    id: number;
    organizationId: number;
    friendlyName: string;
    routingNumber: string;
    accountNumber: string;
    last4: string;
    country: string;
    currency: string;
    plaidAccountId: string;
    plaidTokenId: number;
    plaidBalance: number;
    fcBalance: number;
    /**
     * SD-8906: set by core when a Financial Connections account's connection has been severed at
     * Stripe and can never recover on its own, the account holder has to re-authorize, because a
     * disconnected `fca_` cannot be revived and re-linking mints a new one. While this is set, the
     * account still has an `fcBalance`, but it is FROZEN at whatever it was when the feed died, and
     * transaction import is permanently excluded from the hourly run.
     */
    fcReconnectRequiredAt: Moment;
    plaidImportAfter: Moment;
    sourceType: string;
    stripeFinancialConnectionsId: string;
    stripePaymentMethodId: string;
    createdAt: Moment;
    updatedAt: Moment;
    deletedAt: Moment;

    isTreasuryAccount: boolean = false;
    label: string;
    name: string;

    depositBankAccount: DepositBankAccount = null;
    fixedAsset: FixedAsset = null;
    plaidToken: PlaidToken;
    plaidAccount: {
        accountId: string;
        mask: string;
        name: string;
        officialName: string;
        type: string;
        subtype: string;
    };
    isAllianceAccount: boolean;
    /**
     * SD-8719: whether core can actually READ this account's balance and transactions, appended by
     * UnifiedBankAccount::appendRelatedFields(). Null when the field is absent from the response,
     * so callers can tell "core did not say" apart from a real `false`.
     */
    isBalanceLinked: boolean;
    /**
     * SD-8671: whether core can actually MOVE MONEY on this account, appended by
     * UnifiedBankAccount::appendRelatedFields(). Sibling of `isBalanceLinked` above and read the
     * same way — null when the field is absent from the response, so a UI bundle running against a
     * core that predates the append can fall back rather than treat `undefined` as ineligible.
     * Reading it is what stops the transfer pickers inferring eligibility from which Stripe ids
     * happen to be stored, the inference that silently hid a cohort measured at 17 live rows on
     * 2026-08-19 (provenance: core's `FinancialConnectionsService::findAdoptableExternalAccount()`,
     * which carries the query — a dated sample, not an invariant).
     */
    isTransferEligible: boolean;
    /**
     * SD-9540: the same question for the "To" side only, which is a weaker requirement.
     *
     * `isTransferEligible` governs both directions. This one additionally admits a Connect-created
     * external account Stripe holds only as a `ba_`, because outbound accepts a BankAccount id
     * directly and needs no mandate, while debiting one inbound does. It has to be declared AND
     * assigned here or the flag dies at this boundary: every consumer builds its accounts through
     * `new UnifiedBankAccount(...)`, so an undeclared field silently reads `undefined` downstream and
     * the To picker falls back to the narrower answer — leaving the exact cohort this exists for
     * disabled, with core computing and serialising a flag nobody ever sees.
     */
    isOutboundEligible: boolean;
    /**
     * SD-9078: set while Stripe is waiting for the two small deposits on a manually entered
     * account to be confirmed. Until then Stripe refuses to move money on it, so both transfer
     * guards hold it and the UI names that as the reason rather than showing a bare "unavailable".
     */
    microdepositVerificationRequiredAt: Moment;
    allianceCredential: AllianceCredential;
    allianceTransactionImportEnabled: boolean;
    allianceTransactionImportAfter: Moment;
    unreviewedTransactionsCount: number;
    organization: Organization;
    plaidBalanceHistory: HistoricalBalance[] = [];
    reconciliations: BankReconciliation[];
    lobBankAccounts: LobBankAccount[];

    constructor(unifiedBankAccount: any) {
        const timezone = LegFiJwtService.getTimezone();

        this.id = unifiedBankAccount.id || null;
        this.organizationId = unifiedBankAccount.organizationId || null;
        this.friendlyName = unifiedBankAccount.friendlyName || '';
        this.routingNumber = unifiedBankAccount.routingNumber || '';
        this.accountNumber = unifiedBankAccount.accountNumber || '';
        this.last4 = unifiedBankAccount.last4 || '';
        this.label = this.friendlyName?.trim()
            ? this.friendlyName
            : (this.last4 ? `Account ••••${this.last4}` : 'Bank account');
        this.name = this.label;
        this.country = unifiedBankAccount.country ?? 'US';
        this.currency = unifiedBankAccount.currency ?? 'usd';
        this.unreviewedTransactionsCount = unifiedBankAccount.unreviewedTransactionsCount || 0;
        this.sourceType = unifiedBankAccount.sourceType || null;
        this.isTreasuryAccount = !!unifiedBankAccount.isTreasuryAccount;
        this.stripeFinancialConnectionsId = unifiedBankAccount.stripeFinancialConnectionsId || null;
        this.stripePaymentMethodId = unifiedBankAccount.stripePaymentMethodId || null;
        this.plaidAccountId = unifiedBankAccount.plaidAccountId || null;
        this.plaidAccount = unifiedBankAccount.plaidAccount || null;
        this.plaidTokenId = unifiedBankAccount.plaidTokenId || null;
        this.plaidToken = unifiedBankAccount.plaidToken ? new PlaidToken(unifiedBankAccount.plaidToken) : null;
        this.plaidImportAfter = unifiedBankAccount.plaidImportAfter
                ? moment.utc(unifiedBankAccount.plaidImportAfter, 'YYYY-MM-DD hh:mm:ss').tz(timezone)
                : null;

        this.fcReconnectRequiredAt = unifiedBankAccount.fcReconnectRequiredAt
                ? moment.utc(unifiedBankAccount.fcReconnectRequiredAt, 'YYYY-MM-DD hh:mm:ss').tz(timezone)
                : null;

        this.createdAt = unifiedBankAccount.createdAt
                ? moment.utc(unifiedBankAccount.createdAt, 'YYYY-MM-DD hh:mm:ss').tz(timezone)
                : null;
        this.updatedAt = unifiedBankAccount.updatedAt
                ? moment.utc(unifiedBankAccount.updatedAt, 'YYYY-MM-DD hh:mm:ss').tz(timezone)
                : null;
        this.deletedAt = unifiedBankAccount.deletedAt
                ? moment.utc(unifiedBankAccount.deletedAt, 'YYYY-MM-DD hh:mm:ss').tz(timezone)
                : null;

        if (unifiedBankAccount.depositBankAccount) {
            this.depositBankAccount = new DepositBankAccount(unifiedBankAccount.depositBankAccount);
            this.depositBankAccount.unifiedBankAccount = this;
        }
        if (unifiedBankAccount.fixedAsset) {
            this.fixedAsset = new FixedAsset(unifiedBankAccount.fixedAsset);
            this.fixedAsset.owner = this;
        }
        this.organization = unifiedBankAccount.organization ? new Organization(unifiedBankAccount.organization) : null;
        this.isAllianceAccount = unifiedBankAccount.isAllianceAccount ?? false;
        this.allianceCredential = unifiedBankAccount.allianceCredential
                ? new AllianceCredential(unifiedBankAccount.allianceCredential)
                : null;
        this.allianceTransactionImportEnabled = (this.isAllianceAccount && unifiedBankAccount.allianceTransactionImportEnabled) ?? false;
        this.allianceTransactionImportAfter = unifiedBankAccount.allianceTransactionImportAfter
                ? moment.utc(unifiedBankAccount.allianceTransactionImportAfter, 'YYYY-MM-DD hh:mm:ss').tz(timezone)
                : null;
        if (unifiedBankAccount.plaidBalanceHistory) {
            this.plaidBalanceHistory = unifiedBankAccount.plaidBalanceHistory;
        }
        if (unifiedBankAccount.reconciliations) {
            this.reconciliations = unifiedBankAccount.reconciliations.map((reconciliation: any) => new BankReconciliation(reconciliation));
        }

        // plaidBalance is a database column that's always returned, regardless of
        // whether plaidToken relationship is loaded. Don't gate it on plaidToken presence.
        this.plaidBalance = unifiedBankAccount.plaidBalance ?? null;
        this.fcBalance = unifiedBankAccount.fcBalance ?? null;
        this.isBalanceLinked = unifiedBankAccount.isBalanceLinked ?? null;
        this.isTransferEligible = unifiedBankAccount.isTransferEligible ?? null;
        this.isOutboundEligible = unifiedBankAccount.isOutboundEligible ?? null;
        this.microdepositVerificationRequiredAt = unifiedBankAccount.microdepositVerificationRequiredAt
                ? moment.utc(unifiedBankAccount.microdepositVerificationRequiredAt, 'YYYY-MM-DD hh:mm:ss').tz(timezone)
                : null;

        if (unifiedBankAccount.lobBankAccounts) {
            this.lobBankAccounts = unifiedBankAccount.lobBankAccounts.map(lba => new LobBankAccount(lba));
        }
    }
}
